import { useEffect, useMemo, useState } from 'react';
import { KpiTile } from '../components/KpiTile';
import { parseReportingChartDate } from '../userActivity/formatters';
import { showToast } from '../notifications/toast';
import { UserProfileField } from './UserProfileField';
import { UserAvatar } from '../components/UserAvatar';
import {
  formatLastSignInDisplay,
  resolveLastSignInDisplay
} from './profileActivityDisplay';
import type { UserProfile, UserProfileActivity } from './types';
import { userDisplayName } from './userDisplay';

export interface UserProfilePageProps {
  fetchProfile: () => Promise<UserProfile>;
  fetchProfileActivity?: () => Promise<UserProfileActivity>;
  title?: string;
}

function fieldValue(value: string | null | undefined): string {
  const v = value?.trim();
  return v ? v : '—';
}

function formatMinutes(value: number | null | undefined): string {
  if (value == null) return '—';
  return `${value} min`;
}

function formatCount(value: number): string {
  return value === 0 ? '—' : String(value);
}

function formatTotalUsageMinutes(minutes: number): string {
  if (minutes <= 0) return '';
  if (minutes < 60) return `${minutes} min total`;
  const hours = Math.floor(minutes / 60);
  const rem = minutes % 60;
  return rem > 0 ? `${hours}h ${rem}m total` : `${hours}h total`;
}

function dailyUsageHint(activity: UserProfileActivity, window: 'all' | '90'): string | undefined {
  const days = window === 'all' ? activity.activeDaysAllTime : activity.activeDaysLast90Days;
  const totalMin = window === 'all' ? activity.totalMinutesAllTime : activity.totalMinutesLast90Days;
  const sessions =
    window === 'all' ? activity.totalSessionsAllTime : activity.totalSessionsLast90Days;
  if (days <= 0 || sessions <= 0) return undefined;
  const totalLabel = formatTotalUsageMinutes(totalMin);
  return totalLabel
    ? `${sessions} logins · ${days} active day${days === 1 ? '' : 's'} · ${totalLabel}`
    : `${sessions} logins · ${days} active day${days === 1 ? '' : 's'}`;
}

function formatSourceLabel(source: string): string {
  switch (source.toLowerCase()) {
    case 'graph-app':
      return 'Microsoft Graph (directory)';
    case 'graph':
      return 'Microsoft Graph';
    case 'redis':
      return 'Cached';
    case 'claims':
      return 'Sign-in';
    default:
      return source;
  }
}

export function UserProfilePage({
  fetchProfile,
  fetchProfileActivity,
  title = 'User Profile'
}: UserProfilePageProps) {
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [activity, setActivity] = useState<UserProfileActivity | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activityError, setActivityError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      setActivityError(null);

      const profileTask = fetchProfile()
        .then((data) => {
          if (!cancelled) setProfile(data);
        })
        .catch((err: unknown) => {
          if (!cancelled) {
            setError(err instanceof Error ? err.message : 'Failed to load profile.');
            setProfile(null);
          }
        });

      const activityTask = fetchProfileActivity
        ? fetchProfileActivity()
            .then((stats) => {
              if (!cancelled) setActivity(stats);
            })
            .catch((err: unknown) => {
              if (!cancelled) {
                setActivity(null);
                setActivityError(
                  err instanceof Error ? err.message : 'Failed to load usage statistics.'
                );
              }
            })
        : Promise.resolve().then(() => {
            if (!cancelled) setActivity(null);
          });

      await Promise.all([profileTask, activityTask]);
      if (!cancelled) setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [fetchProfile, fetchProfileActivity]);

  useEffect(() => {
    if (error) showToast(error, 'error');
  }, [error]);

  const username = profile?.userPrincipalName ?? profile?.mail ?? null;

  const dailySessionsChart = useMemo(
    () =>
      (activity?.dailySessionsLast7Days ?? []).map((point) => ({
        date: parseReportingChartDate(point.date),
        sessionCount: point.sessionCount
      })),
    [activity?.dailySessionsLast7Days]
  );

  const hasAnySessions =
    activity != null &&
    (activity.totalSessionsAllTime > 0 || activity.totalSessionsLast90Days > 0);

  const lastSignIn = useMemo(
    () => (activity ? resolveLastSignInDisplay(activity) : null),
    [activity]
  );

  const primaryEmail = profile?.mail?.trim() || profile?.userPrincipalName?.trim() || null;

  const sourceLabels = useMemo(() => {
    const seen = new Set<string>();
    const out: string[] = [];
    for (const s of profile?.sources ?? []) {
      const key = s.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(formatSourceLabel(s));
    }
    return out;
  }, [profile?.sources]);

  return (
    <div className="user-profile-page">
      <h2 className="user-profile-page__title">{title}</h2>
      {loading ? (
        <div className="user-profile-page__loading">
          <div className="dashboard-loading-spinner" aria-hidden />
        </div>
      ) : profile ? (
        <div className="user-profile-page__stack">
          <header className="user-profile-page__hero user-profile-page__card">
            <UserAvatar
              username={username}
              displayName={profile.displayName}
              hasPhoto={profile.hasPhoto}
              size="lg"
              className="user-profile-page__avatar"
            />
            <div className="user-profile-page__hero-text">
              <div className="user-profile-page__name">
                {userDisplayName(username, profile.displayName)}
              </div>
              {primaryEmail ? (
                <div className="user-profile-page__upn">{primaryEmail}</div>
              ) : null}
              {profile.highestRole ? (
                <div className="user-profile-page__badges">
                  <span className="user-profile-badge user-profile-badge--primary">
                    {profile.highestRole}
                  </span>
                  {profile.roles
                    .filter((r) => r !== profile.highestRole)
                    .map((role) => (
                      <span key={role} className="user-profile-badge">
                        {role}
                      </span>
                    ))}
                </div>
              ) : null}
              {sourceLabels.length > 0 ? (
                <div className="user-profile-page__sources" aria-label="Profile data sources">
                  {sourceLabels.map((label) => (
                    <span key={label} className="user-profile-source-chip">
                      {label}
                    </span>
                  ))}
                </div>
              ) : null}
            </div>
            {fetchProfileActivity ? (
              <div className="user-profile-page__hero-aside" aria-label="Last sign-in on this site">
                <div className="user-profile-page__hero-aside-label">Last sign-in</div>
                {activityError ? (
                  <div className="user-profile-page__hero-aside-value user-profile-page__hero-aside-value--muted">
                    Unavailable
                  </div>
                ) : lastSignIn ? (
                  <>
                    <div className="user-profile-page__hero-aside-value">
                      {formatLastSignInDisplay(lastSignIn)}
                    </div>
                    {lastSignIn.dateOnly ? (
                      <div className="user-profile-page__hero-aside-hint">
                        Latest day with sign-ins (exact time unavailable)
                      </div>
                    ) : activity?.reportingTimeZone ? (
                      <div className="user-profile-page__hero-aside-hint">
                        Shown in {activity.reportingTimeZone}
                      </div>
                    ) : null}
                  </>
                ) : activity && activity.totalSessionsAllTime > 0 ? (
                  <div className="user-profile-page__hero-aside-value user-profile-page__hero-aside-value--muted">
                    Recently on this site
                  </div>
                ) : activity ? (
                  <div className="user-profile-page__hero-aside-value user-profile-page__hero-aside-value--muted">
                    No sign-ins recorded on this site
                  </div>
                ) : (
                  <div className="user-profile-page__hero-aside-value user-profile-page__hero-aside-value--muted">
                    —
                  </div>
                )}
              </div>
            ) : null}
          </header>

          {fetchProfileActivity ? (
            <section
              className="user-profile-page__card user-profile-page__usage-panel"
              aria-labelledby="user-profile-usage"
            >
              <h3 id="user-profile-usage" className="user-profile-section__title">
                Usage on this site
              </h3>
              <p className="user-profile-section__hint">
                Session statistics apply to this application only (not other Jarvis sites).
                {activity?.reportingTimeZone
                  ? ` Days and averages use ${activity.reportingTimeZone}.`
                  : null}{' '}
                Avg daily usage adds up every session on each day you signed in, so it is higher than
                avg session length when you sign in multiple times per day.
              </p>
              {activityError ? (
                <p className="user-profile-page__inline-warn" role="status">
                  {activityError}
                </p>
              ) : activity ? (
                <div className="user-profile-usage-layout">
                  <div className="user-profile-usage-kpis">
                    {!hasAnySessions ? (
                      <p className="user-profile-section__hint">
                        No sessions recorded yet on this site.
                      </p>
                    ) : null}
                    <p className="user-profile-kpi-group-label">All time</p>
                    <div className="user-profile-kpi-grid">
                      <KpiTile
                        label="Total logins"
                        value={formatCount(activity.totalSessionsAllTime)}
                      />
                      <KpiTile
                        label="Avg session"
                        value={formatMinutes(activity.averageSessionMinutesAllTime)}
                      />
                      <KpiTile
                        label="Avg daily usage"
                        value={formatMinutes(activity.averageMinutesPerActiveDayAllTime)}
                        hint={
                          activity.averageMinutesPerActiveDayAllTime != null
                            ? dailyUsageHint(activity, 'all')
                            : undefined
                        }
                      />
                    </div>
                    <p className="user-profile-kpi-group-label">Last 90 days</p>
                    <div className="user-profile-kpi-grid">
                      <KpiTile
                        label="Logins"
                        value={formatCount(activity.totalSessionsLast90Days)}
                      />
                      <KpiTile
                        label="Avg session"
                        value={formatMinutes(activity.averageSessionMinutesLast90Days)}
                      />
                      <KpiTile
                        label="Avg daily usage"
                        value={formatMinutes(activity.averageMinutesPerActiveDayLast90Days)}
                        hint={
                          activity.averageMinutesPerActiveDayLast90Days != null
                            ? dailyUsageHint(activity, '90')
                            : undefined
                        }
                      />
                      <KpiTile
                        label="Active days"
                        value={formatCount(activity.activeDaysLast90Days)}
                      />
                    </div>
                  </div>
                  {dailySessionsChart.length > 0 ? (
                    <div className="user-profile-usage-chart">
                      <h4 className="user-profile-chart__title">Sessions (last 7 days)</h4>
                      <div className="user-profile-sessions-bars" role="img" aria-label="Sessions last 7 days">
                        {(() => {
                          const max = Math.max(
                            1,
                            ...dailySessionsChart.map((p) => p.sessionCount)
                          );
                          return dailySessionsChart.map((point) => {
                            const label = point.date.toLocaleDateString(undefined, {
                              month: 'short',
                              day: 'numeric'
                            });
                            const heightPct = Math.max(8, Math.round((point.sessionCount / max) * 100));
                            return (
                              <div key={point.date.toISOString()} className="user-profile-sessions-bars__col">
                                <div
                                  className="user-profile-sessions-bars__bar"
                                  style={{ height: `${heightPct}%` }}
                                  title={`${label}: ${point.sessionCount}`}
                                />
                                <span className="user-profile-sessions-bars__label">{label}</span>
                              </div>
                            );
                          });
                        })()}
                      </div>
                    </div>
                  ) : null}
                </div>
              ) : null}
            </section>
          ) : null}

          <div className="user-profile-details-grid">
            <section
              className="user-profile-page__card user-profile-details-card"
              aria-labelledby="user-profile-organization"
            >
              <h3 id="user-profile-organization" className="user-profile-section__title">
                Organization
              </h3>
              <div className="user-profile-fields user-profile-fields--two-col">
                <UserProfileField label="Department" value={fieldValue(profile.department)} />
                <UserProfileField label="Job title" value={fieldValue(profile.jobTitle)} />
                <UserProfileField label="Company" value={fieldValue(profile.company)} />
                <UserProfileField label="Office" value={fieldValue(profile.officeLocation)} />
                <UserProfileField label="Employee ID" value={fieldValue(profile.employeeId)} />
                <UserProfileField label="Mobile phone" value={fieldValue(profile.mobilePhone)} />
              </div>
            </section>
          </div>
        </div>
      ) : (
        <p className="user-profile-page__error">Profile could not be loaded.</p>
      )}
    </div>
  );
}
